import styled, { css } from 'styled-components';
import { ButtonBox } from '@/components/UI/Button/Button.styles';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import {
  InputAppearance,
  InputLabelPosition,
} from '@/components/UI/Input/Input.types';
import {
  INPUT_CLEAR_BUTTON_HEIGHT,
  INPUT_DEFAULT_PADDING_BOTTOM,
  INPUT_DEFAULT_PADDING_TOP,
  INPUT_HEIGHT,
} from '@/components/UI/Input/Input.constants';

function getInputStylesForAppearance(
  appearance?: InputAppearance,
  hasAdditionalPadding?: boolean,
  withClear?: boolean
) {
  switch (appearance) {
    case 'outlined': {
      return css`
        padding-left: 55px;
        padding-right: 45px;
        font-size: 16px;
        border-radius: 14px;
        border: 1px solid ${({ theme }) => theme.palette.secondary.border};

        @media (max-width: 949px) {
          padding-left: 45px;
          padding-right: 37px;
          font-size: 15px;
        }

        &:focus {
          border: 1px solid ${({ theme }) => theme.palette.brand.main};
        }
      `;
    }

    case 'filled': {
      const paddingRight = withClear ? 45 : 15;
      return css`
        font-size: 16px;
        padding: 0 ${hasAdditionalPadding ? 55 : paddingRight}px 0 15px;
      `;
    }

    default: {
      return css``;
    }
  }
}

function getInputBoxPadding(
  withClear?: boolean,
  isBigText?: boolean,
  customPaddingTop?: number,
  customPaddingBottom?: number
) {
  const paddingTop = customPaddingTop ?? INPUT_DEFAULT_PADDING_TOP;
  const paddingBottom = customPaddingBottom ?? INPUT_DEFAULT_PADDING_BOTTOM;
  if (withClear) {
    return `${paddingTop}px 20px ${paddingBottom}px 0`;
  }
  if (isBigText) {
    return '8px 0 0 0';
  }
  return `${paddingTop}px 0 ${paddingBottom}px 0`;
}

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const InputFilledWrapper = styled(InputWrapper)<{
  $isFocused?: boolean;
  $hasError?: boolean;
  $labelPosition?: InputLabelPosition;
}>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  height: 54px;
  border-radius: 14px;

  ${({ $labelPosition }) =>
    $labelPosition === 'left' &&
    css`
      flex-direction: row;
      gap: 10px;
      height: 44px;
    `}

  border: 1px solid
    ${({ theme, $isFocused, $hasError }) => {
    if ($hasError) return theme.palette.system.red;
    if ($isFocused) return theme.palette.brand.main;
    return theme.palette.primary.border;
  }};
  background-color: ${({ theme, $isFocused, $hasError }) => {
    if ($hasError) return theme.palette.system.redToWhite;
    if ($isFocused) return theme.palette.primary.background;
    return theme.palette.quartenary.background;
  }};
`;

export const InputOutlinedBg = styled.div.attrs(() => ({
  'aria-label': 'outlined background',
}))`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
`;

export const SearchLeftIconWrapper = styled.div.attrs<{
  $appearance?: InputAppearance;
}>(() => ({
  'aria-label': 'search icon',
}))`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $appearance }) => ($appearance === 'outlined' ? '20px' : '10px')};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 949px) {
    left: ${({ $appearance }) =>
      $appearance === 'outlined' ? '15px' : '10px'};
  }
`;

export const InputBox = styled.input<{
  $appearance?: InputAppearance;
  $withClear?: boolean;
  $hasBorderBottom?: boolean;
  $isBigText?: boolean;
  $customPaddingTop?: number;
  $customPaddingBottom?: number;
  $isShifted?: boolean;
  $withEllipsis?: boolean;
  $hasAdditionalPadding?: boolean;
  $labelPosition?: InputLabelPosition;
}>`
  position: relative;
  z-index: 2;
  background-color: transparent;
  border: 0;
  border-bottom: ${({ $appearance }) =>
    $appearance === 'outlined' ? 'none' : '1px solid transparent'};
  padding: ${({
    $withClear,
    $isBigText,
    $customPaddingTop,
    $customPaddingBottom,
  }) =>
    getInputBoxPadding(
      $withClear,
      $isBigText,
      $customPaddingTop,
      $customPaddingBottom
    )};
  outline: 0;
  font-size: ${({ $isBigText }) => ($isBigText ? '24px' : '15px')};
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: ${({ $isBigText }) => ($isBigText ? 500 : 400)};
  line-height: normal;
  color: ${({ theme }) => theme.palette.primary.labels};
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::placeholder {
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.palette.secondary.labels};
  }

  &:disabled {
    color: ${({ theme }) => theme.palette.primary.labels};
    -webkit-text-fill-color: ${({ theme }) => theme.palette.primary.labels};
    opacity: 1;
  }

  ${({ $labelPosition }) =>
    $labelPosition === 'left' &&
    css`
      top: 50%;
      transform: translateY(-50%);
      padding: 10px 0;
    `}

  ${({ $hasBorderBottom }) =>
    $hasBorderBottom &&
    css`
      border-bottom-color: ${({ theme }) => theme.palette.primary.border};
    `}

  ${({ $isShifted, $labelPosition }) =>
    $isShifted &&
    $labelPosition === 'top' &&
    css`
      margin-top: 3px;
    `}

  ${({ $withEllipsis }) =>
    $withEllipsis &&
    css`
      text-overflow: ellipsis;
    `}

  ${({ $appearance, $hasAdditionalPadding, $withClear }) =>
    getInputStylesForAppearance($appearance, $hasAdditionalPadding, $withClear)}
`;

export const InputClear = styled(ButtonBox).attrs<{
  $customPaddingTop?: number;
  $customPaddingRight?: number;
}>(() => ({
  type: 'button',
  $appearance: 'transparent',
  $shape: 'square',
  'aria-label': 'clear',
}))<{
  $hasLeftPadding?: boolean;
  $isYCentered?: boolean;
}>`
  position: absolute;
  top: ${({ $customPaddingTop }) =>
    typeof $customPaddingTop === 'number'
      ? `${$customPaddingTop}px`
      : `calc(${INPUT_HEIGHT}px / 2 - (${INPUT_CLEAR_BUTTON_HEIGHT}px / 2))`};
  right: ${({ $hasLeftPadding, $customPaddingRight }) => {
    if (typeof $customPaddingRight === 'number') {
      return `${$customPaddingRight}px`;
    }
    return $hasLeftPadding ? '10px' : '0';
  }};
  z-index: 3;
  background-image: url('/images/close-filled.svg');
  height: ${INPUT_CLEAR_BUTTON_HEIGHT}px;
  width: ${INPUT_CLEAR_BUTTON_HEIGHT}px;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  cursor: pointer;

  ${({ $isYCentered }) =>
    $isYCentered &&
    css`
      top: 50%;
      transform: translateY(-50%);
    `}
`;

export const InputLabel = styled(Typography.Title)<{
  $isShifted?: boolean;
}>`
  color: ${({ theme }) => theme.palette.secondary.labels};
  position: relative;
  top: ${({ $isShifted }) => ($isShifted ? '7.5px' : '21.5px')};
  left: 0;
  z-index: 1;
  transition: top 0.3s ease-in-out;
`;

export const InputFilledLabel = styled(Typography.Title)<{
  $isShifted?: boolean;
  $hasError?: boolean;
  $labelPosition?: InputLabelPosition;
}>`
  color: ${({ theme, $hasError }) =>
    $hasError ? theme.palette.system.red : theme.palette.secondary.labels};
  position: relative;
  top: ${({ $isShifted }) => ($isShifted ? '7.5px' : '17.5px')};
  margin-bottom: ${({ $isShifted }) => ($isShifted ? '3px' : '0')};
  left: 15px;
  z-index: 1;
  transition: top 0.3s ease-in-out;

  ${({ $labelPosition }) =>
    $labelPosition === 'left' &&
    css`
      display: flex;
      align-items: center;
      top: 50%;
      transform: translateY(-50%);
      font-size: 16px;
      color: ${({ theme }) => theme.palette.primary.labels};
    `}
`;
